<script lang="ts">
import { browser } from '$app/environment'
import { page } from '$app/state'
import StudyChapterProblemSlide from '$lib/components/study/slide_types/StudyChapterProblemSlide.svelte'
import StudyChoroplethSlide from '$lib/components/study/slide_types/StudyChoroplethSlide.svelte'
import StudyClosingSlide from '$lib/components/study/slide_types/StudyClosingSlide.svelte'
import StudyCoverSlide from '$lib/components/study/slide_types/StudyCoverSlide.svelte'
import StudyDiagnosisBarInsightSlide from '$lib/components/study/slide_types/StudyDiagnosisBarInsightSlide.svelte'
import StudyDistributionSlide from '$lib/components/study/slide_types/StudyDistributionSlide.svelte'
import StudyMarketBattleSlide from '$lib/components/study/slide_types/StudyMarketBattleSlide.svelte'
import StudyMarketOutlookSlide from '$lib/components/study/slide_types/StudyMarketOutlookSlide.svelte'
import StudyMarketStatsSlide from '$lib/components/study/slide_types/StudyMarketStatsSlide.svelte'
import StudyMeetImxDataSlide from '$lib/components/study/slide_types/StudyMeetImxDataSlide.svelte'
import StudyMixCompare1Slide from '$lib/components/study/slide_types/StudyMixCompare1Slide.svelte'
import StudyMixCompare2Slide from '$lib/components/study/slide_types/StudyMixCompare2Slide.svelte'
import StudyPatientDemographicsSlide from '$lib/components/study/slide_types/StudyPatientDemographicsSlide.svelte'
import StudyScoreBoardSlide from '$lib/components/study/slide_types/StudyScoreBoardSlide.svelte'
import StudySpendTrajectorySlide from '$lib/components/study/slide_types/StudySpendTrajectorySlide.svelte'
import StudyTableSlide from '$lib/components/study/slide_types/StudyTableSlide.svelte'
import StudyPresentation from '$lib/components/study/StudyPresentation.svelte'
import { colorPalette } from '$lib/utils/report/chart-config'
import type { PageData } from './$types'
import { calcDisplayStats, formatCount, formatSpend } from './calculations'

let { data }: { data: PageData } = $props()

/** When true (?disable_links in URL), end-slide links (Explore/Contact) are hidden; Restart Story stays.
 *  `browser` guard is required: this page is prerendered (see src/routes/+layout.js). */
const disableLinks = $derived(browser && !!page.url.searchParams.get('disable_links'))

// ── Reactive state ──────────────────────────────────────────────────────────
const quarterKeys = $derived(Object.keys(data.quarterlyData).sort())
const stats = $derived(calcDisplayStats(data, quarterKeys))

// ── Precomputed chart data ───────────────────────────────────────────────────
const mfrYears = $derived(Object.keys(data.manufacturerYearlyData).sort())
const mfrPayColors = ['#a78bfa', '#11a0a5', '#ffd166', '#E8634A']
const mfrPayColorsWithOther = [...mfrPayColors, '#6b7280']
const manufacturerPayTop4WithOther = $derived(
  (() => {
    const all = data.manufacturerPay
    if (all.length <= 4) return all
    const top4 = all.slice(0, 4)
    const otherSum = all.slice(4).reduce((s, [, v]) => s + v, 0)
    return otherSum > 0 ? [...top4, ['Other', otherSum] as [string, number]] : top4
  })(),
)
const mfrPayFillColors = [
  'rgba(167,139,250,.13)',
  'rgba(17,160,165,.13)',
  'rgba(255,209,102,.1)',
  'rgba(232,99,74,.12)',
]
const top4MfrNames = $derived(data.manufacturerClaims.slice(0, 4).map(([name]) => name))
const topTenStates = $derived(
  (Object.entries(data.stateData) as [string, number][]).sort((a, b) => b[1] - a[1]).slice(0, 10),
)
const mfrClaimTotal = $derived(data.manufacturerClaims.reduce((s, [, c]) => s + c, 0))
const manufacturerClaimsTop4WithOther = $derived(
  (() => {
    const all = data.manufacturerClaims
    if (all.length <= 4) return all
    const top4 = all.slice(0, 4)
    const otherSum = all.slice(4).reduce((s, [, v]) => s + v, 0)
    return otherSum > 0 ? [...top4, ['Other', otherSum] as [string, number]] : top4
  })(),
)
const top4MfrSharePct = $derived(
  mfrClaimTotal > 0
    ? (
        (data.manufacturerClaims.slice(0, 4).reduce((s, [, c]) => s + c, 0) / mfrClaimTotal) *
        100
      ).toFixed(1)
    : '0',
)
/** Slide s6: manufacturer mix segments for `StudyMixCompare1Slide`. */
const adcS6MixSegments = $derived(
  manufacturerClaimsTop4WithOther.map(([name, count], i) => ({
    name,
    count,
    color: mfrPayColorsWithOther[i % mfrPayColorsWithOther.length],
  })),
)
const payTotal = $derived(data.payerData.reduce((s, [, c]) => s + c, 0))
/** Slide s6b: payer rows for `StudyMixCompare2Slide` (bar vs tile colors match prior inline slide). */
const adcS6bPayerRows = $derived(
  data.payerData.map(([name, count], i) => ({
    name,
    count,
    barColor: name === 'Cash Pay' ? 'rgba(232,99,74,.88)' : colorPalette[i % colorPalette.length],
    tileStripeColor: colorPalette[i % colorPalette.length],
  })),
)
const cashPayTotal = $derived(data.payerData.find(([name]) => name === 'Cash Pay')?.[1] ?? 0)
const top5Pct = $derived(
  data.stats.totalClaims > 0
    ? (
        (data.stats.topStates.reduce((s, [, c]) => s + c, 0) / data.stats.totalClaims) *
        100
      ).toFixed(0)
    : '0',
)

/** Slide s4: geographic scoreboard (`StudyScoreBoardSlide`). */
const adcS4SpotlightRows = $derived(
  data.stats.topStates.slice(0, 3).map(([state, claims]) => ({
    state,
    claims,
    valueLabel: formatCount(claims),
  })),
)
const adcS4LeaderboardRows = $derived(
  topTenStates.map(([state, claims]) => ({
    state,
    claims,
    valueLabel: formatCount(claims),
  })),
)

const adcS4InsightHtml =
  "<strong>Real insight:</strong> When Enhertu's HER2-low indication launched, IMX Data showed NE and Mid-Atlantic adoption at 2&times; the national average within one quarter &mdash; reflecting dense academic oncology networks before national access reports surfaced."

const adcS4CalloutBodyHtml =
  'of all ADC claims concentrated<br />in just <strong style="color:var(--g);">5 states</strong>'

/** Slide s9: closing footer (`StudyClosingSlide` `footerHtml`). */
const adcClosingFooterHtml = `<details class="adc-key-sources">
  <summary>Key sources</summary>
  <ul>
    <li>
      <a href="https://www.fda.gov" target="_blank" rel="noopener noreferrer">U.S. FDA</a>
      &mdash; approvals and regulatory communications
    </li>
    <li>
      <a
        href="https://www.astrazeneca.com/investor-relations.html"
        target="_blank"
        rel="noopener noreferrer">AstraZeneca</a
      >
      &mdash; financial results and product revenue (e.g. Enhertu U.S. performance)
    </li>
    <li>
      <a href="https://www.pfizer.com/news" target="_blank" rel="noopener noreferrer">Pfizer</a>
      &mdash; Seagen acquisition and pipeline updates
    </li>
    <li>
      <a href="https://investors.abbvie.com" target="_blank" rel="noopener noreferrer">AbbVie</a>
      &mdash; ImmunoGen transaction and oncology disclosures
    </li>
    <li>
      <a href="https://www.fiercebiotech.com" target="_blank" rel="noopener noreferrer">Fierce Biotech</a>
      &mdash; industry news and deal coverage
    </li>
    <li>
      Third-party market sizing and CAGR estimates (industry research and forecast providers
      such as Statifacts, Statista, and similar published market reports)
    </li>
    <li>
      Clinical trial and congress disclosures (e.g. DESTINY-Breast06 and related oncology
      data)
    </li>
  </ul>
</details>`

/** Slide s7b: horizontal bar chart — largest diagnosis at top */
const diagnosisChartRows = $derived([...data.diagnosisTopDiagnoses].reverse())

/** Slide s7b: HCPCS J-code + brand (compact subtext under diagnosis chart). */
const slideS7bAdcHcpcs = [
  { code: 'J9042', brand: 'Adcetris' },
  { code: 'J9273', brand: 'Tivdak' },
  { code: 'J9177', brand: 'Padcev' },
  { code: 'J9063', brand: 'Elahere' },
  { code: 'J9358', brand: 'Enhertu' },
] as const

const adcS7bChartSubtitleJcodesHtml = $derived.by(() => {
  const jParts = slideS7bAdcHcpcs
    .map(
      (row, i) =>
        `${i > 0 ? '<span> &middot; </span>' : ''}<code style="font-family:'DM Mono',monospace;font-size:0.62rem;color:var(--amb);">${row.code}</code> ${row.brand}`,
    )
    .join('')
  return `ICD-10-CM Diagnosis Codes Associated with ADC J-codes billable drugs on the claim: ${jParts}<br />(Hover to see diagnosis descriptions)`
})

const adcS7NarrativeCards = $derived([
  {
    icon: '👥',
    title: `${stats.topAgeLabel} Cohort Dominates`,
    text: `The ${stats.topAgeLabel} birth decade leads &mdash; consistent with ADC use concentrated in breast cancer and urothelial cancer patient populations. Younger cohort penetration is the key expansion thesis.`,
    footerValue: stats.topAgeK,
    footerCaption: `${stats.topAgeLabel} cohort &middot; highest volume segment`,
  },
  {
    icon: '⚡',
    title: 'Female-Dominant — For Now',
    text: `Breast cancer ADC use drives the ${stats.femalePct}% female skew. As urothelial, lung, and gastric indications expand (Padcev, Enhertu NSCLC, gastric HER2+), the gender split will rebalance &mdash; trackable in IMX demographics before analyst models update.`,
  },
])

const adcS7InsightHtml =
  "<strong>Why it matters:</strong> As Enhertu's HER2-low indication expands the eligible population, demographic shifts are the earliest signal of real-world adoption breadth."

const adcS8SpendLineValues = $derived(quarterKeys.map((q) => data.quarterlyData[q].total_pay / 1e6))
const adcS8SignalCalloutHtml = $derived(
  `<strong style="color:var(--g);">IMX Data is the early warning system.</strong> Every label expansion, every formulary change, every acquisition's prescribing footprint &mdash; visible in claims data before it becomes consensus. ${stats.quarterCount} quarters of proof.`,
)

/** Slide s8: M&amp;A timeline (`StudyMarketOutlookSlide`). */
const adcS8TimelineItems = [
  {
    period: 'Nov 2023',
    bodyHtml:
      "<strong>AbbVie acquires ImmunoGen for $10.1B.</strong> ELAHERE (mirvetuximab) becomes AbbVie's ADC anchor. Strategic validation of folate receptor &alpha; targeting for ovarian cancer.",
  },
  {
    period: 'Dec 2023',
    bodyHtml:
      '<strong>BMS / SystImmune deal (up to $8.4B).</strong> $800M upfront to co-develop BL-B01D1 &mdash; a potentially first-in-class bispecific ADC targeting HER2&times;TROP2.',
  },
  {
    period: 'Dec 2023',
    bodyHtml:
      "<strong>Pfizer closes Seagen acquisition for $43B.</strong> Adcetris, Padcev, Tivdak, and Tukysa enter Pfizer's portfolio. ADC pipeline doubles to 60 programs.",
  },
  {
    period: 'Mar 2024',
    bodyHtml:
      "<strong>J&amp;J acquires Ambrx for $2B.</strong> Next-generation site-specific ADC platform adds precision conjugation IP to J&amp;J's oncology pipeline.",
  },
  {
    period: 'Jan 2025',
    bodyHtml:
      '<strong>Enhertu HER2-low/ultralow approval.</strong> $2,446M U.S. FY2025 sales (+32% YoY). AstraZeneca recognizes a $175M milestone payment. Eligible patient population expands materially.',
  },
  {
    period: '2026',
    bodyHtml:
      '<strong>Pfizer pipeline pruning.</strong> Discontinued disitamab vedotin in bladder cancer; sharpened focus on Padcev in urothelial disease. Two other early Seagen ADC programs also ended &mdash; portfolio concentration over breadth.',
  },
  {
    period: '2026–34',
    bodyHtml:
      '<strong>$32.11B global ADC market by 2033.</strong> CAGR: 11.6% from $12.26B in 2024. U.S. market reaches ~$12.05B by 2034. IMX provides the ground truth behind every forecast.',
  },
] as const

/** Slide s1b: ADC vs. conventional chemo — shared copy for table + mobile cards */
const adcChemoCompareRows = [
  {
    dimension: 'Targeting',
    conventional: 'Broad cytotoxicity toward rapidly dividing cells — limited tumor selectivity.',
    adc: 'Antibody-directed delivery to tumor-associated antigens on cancer cells.',
  },
  {
    dimension: 'Mechanism',
    conventional:
      'Direct cell killing via classic cytotoxic mechanisms (e.g. DNA damage, tubulin disruption).',
    adc: 'mAb + linker + payload; binding, internalization, and intracellular payload release.',
  },
  {
    dimension: 'Cellular impact',
    conventional: 'Affects malignant and healthy proliferating tissues (e.g. marrow, GI mucosa).',
    adc: 'Preferential payload delivery to antigen-expressing tumor cells vs. many normal tissues.',
  },
  {
    dimension: 'OS / PFS',
    conventional: 'Established benchmarks for each indication and line of therapy.',
    adc: 'Meta-analyses: often improved OS and PFS vs. standard chemo — without higher overall toxicity in aggregate.',
  },
  {
    dimension: 'Toxicity',
    conventional: 'Systemic, dose-limiting toxicities (e.g. myelosuppression, GI, neuropathy).',
    adc: 'Potentially reduced off-target payload exposure; agent-specific risks (e.g. ocular, ILD) remain important.',
  },
] as const

/** Slide s3b: theme → IMX dictionary fields (full product schema vs this study’s aggregates) */
const imxThemeFieldRows = [
  {
    theme: 'Product identity',
    fields: 'manufacturer, drug_generic_name, drug_name, ndc',
  },
  {
    theme: 'Patient geography',
    fields: 'patient_state (aggregated as location in oncology exports)',
  },
  {
    theme: 'Spend & payment',
    fields: 'ingredient_cost_paid, payment_type, charge fields',
  },
  {
    theme: 'Payer & plan',
    fields: 'payer_name, plan_type_description, plan_type',
  },
  {
    theme: 'Clinical coding',
    fields: 'diagnosis_code, procedure_code',
  },
  {
    theme: 'Workflow & providers',
    fields: 'reject_code, daw_code, billing NPI, referring NPI',
  },
  {
    theme: 'Therapeutic class',
    fields: 'usc_class_name, usc_class_code',
  },
] as const

/** Slide s3b: footnote under IMX dictionary table (`StudyTableSlide`). */
const adcImxDictionaryFootnoteHtml =
  '<strong style="color:var(--g);">This deck</strong> uses aggregated columns from the oncology exports: <code style="font-size:.68rem;color:var(--amb);">location</code>, <code style="font-size:.68rem;color:var(--amb);">quarter</code>, <code style="font-size:.68rem;color:var(--amb);">total_claims</code>, <code style="font-size:.68rem;color:var(--amb);">total_charge_amount</code>, <code style="font-size:.68rem;color:var(--amb);">manufacturer_claims</code>, <code style="font-size:.68rem;color:var(--amb);">drug_claims</code>, <code style="font-size:.68rem;color:var(--amb);">manufacturer</code>, <code style="font-size:.68rem;color:var(--amb);">drug_name</code>, <code style="font-size:.68rem;color:var(--amb);">payment_types</code>, <code style="font-size:.68rem;color:var(--amb);">patient_genders</code>, and <code style="font-size:.68rem;color:var(--amb);">patient_birth_years_10</code> &mdash; not the full claim-level schema in every figure.'

/** Slide s5: agent volume insight (`StudyBlueCalloutCard`). */
const adcS5DrugMixCalloutHtml =
  "<strong>Enhertu's trajectory is the defining signal.</strong> The Phase III DESTINY-Breast06 trial showed median PFS &gt;1 year and a 36% reduction in disease progression or death vs. chemotherapy (ORR: 62.6% vs. 34.4%). The Jan 27, 2025 FDA approval for HER2-low/ ultralow breast cancer expanded the eligible population dramatically &mdash; visible in claims data the same quarter. AstraZeneca reported $1,176M U.S. product revenue from Enhertu in FY2025 ($2,446M total U.S. in-market; +32% YoY)."

/** Slide s3: Meet IMX Data — imx-grid tiles + narrative cards. */
const adcMeetImxGridTiles = $derived([
  { variant: 'imx-grid' as const, value: '50', label: 'U.S. States' },
  { variant: 'imx-grid' as const, value: String(stats.quarterCount), label: 'Quarters of Data' },
  {
    variant: 'imx-grid' as const,
    value: String(data.topDrugs.length),
    label: 'ADC Agents Tracked',
  },
  {
    variant: 'imx-grid' as const,
    value: String(data.manufacturerClaims.length),
    label: 'Manufacturers Tracked',
  },
  { variant: 'imx-grid' as const, value: String(data.payerData.length), label: 'Payer Segments' },
])

const adcMeetImxTextCards = $derived([
  {
    title: '📍 Zip-Code-Level Resolution',
    text: 'All 50 U.S. states and anonymized zip codes. Real ADC prescribing patterns &mdash; from academic cancer centers to community oncology networks, every quarter.',
  },
  {
    title: `📅 ${stats.quarterCount} Quarters of ADC History`,
    text: 'Every approval, every label expansion, every acquisition footprint is visible in the prescribing data.',
  },
  {
    title: '🔬 Drug-Level Competitive Intelligence',
    text: 'Enhertu vs. Adcetris vs. Padcev vs. Trodelvy &mdash; claims, spend, and share broken out by agent.',
  },
  {
    title: '🏭 Manufacturer & Market Share',
    text: 'Pfizer/Seagen, AstraZeneca/Daiichi, AbbVie &mdash; M&A and commercial execution show up in claims first. IMX tracks manufacturer share day by day.',
  },
  {
    title: '💵 Payer Mix Breakdown',
    text: 'Medicare, commercial, Medicaid, and cash &mdash; payment type mix by quarter from oncology claims. IMX tracks every payer segment.',
  },
  {
    title: '🧬 Full Patient Demographics',
    text: 'Birth year and gender breakdowns &mdash; essential for modeling real-world patient populations behind the trial data.',
  },
])
</script>

<svelte:head>
  <title>{data.metadata.title} | IMX Data</title>
  <meta name="description" content={data.metadata.description} />
  <meta property="og:title" content="{data.metadata.title} | IMX Data" />
  <meta property="og:description" content={data.metadata.description} />
  <meta property="og:type" content="article" />
  <meta property="article:published_time" content={data.metadata.publishedDate} />
  <meta property="article:modified_time" content={data.metadata.updatedDate} />
  <link rel="canonical" href={data.metadata.canonicalUrl} />
  {@html `<script type="application/ld+json">${JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: data.metadata.title,
    description: data.metadata.description,
    url: data.metadata.canonicalUrl,
    datePublished: data.metadata.publishedDate,
    dateModified: data.metadata.updatedDate,
    temporalCoverage: data.metadata.dataThrough,
    inLanguage: 'en',
    creator: data.metadata.authors.map((a) => ({ '@type': 'Person', name: a.name, roleName: a.role })),
    publisher: {
      '@type': 'Organization',
      name: 'IMX Data, LLC',
      url: 'https://www.imxresearch.com',
      logo: { '@type': 'ImageObject', url: 'https://www.imxresearch.com/webflow/images/logo-v2-wht.svg' },
    },
    isAccessibleForFree: false,
    license: 'https://www.imxresearch.com/research',
  })}<\/script>`}
</svelte:head>

<StudyPresentation title="{data.metadata.title} | IMX Data" meta={data.metadata}>
  {#snippet children({ goTo, activeSlideId, slideActive })}
    <!-- SLIDE 0: COVER -->
    <StudyCoverSlide
      id="s0"
      tagline={{
        line1: 'The ADC Revolution',
        line2Emphasis: 'Quantifying the Shift',
      }}
      title="ADC Oncology"
      titleFontRem={3.5}
      subtitle={`${stats.quarterCount} quarters of real-world claims intelligence.`}
      body="15 approved therapies. 200+ in the pipeline. $43B in M&A. Here's what the data shows."
    />

    <!-- SLIDE 1: THE PROBLEM -->
    <StudyChapterProblemSlide
      id="s1"
      active={slideActive('s1')}
      eyebrow="Chapter 01 &middot; The Challenge"
      headline="From Blunt Force Chemotherapy<br />to <em>Targeted Biological Missiles</em>"
      textCards={[
        {
          icon: '🎯',
          title: 'Targeted Chemotherapy',
          text: 'Conventional chemo broadly targets all rapidly dividing cells. ADCs chemically link a monoclonal antibody to a cytotoxic payload &mdash; delivering lethal precision directly to tumor cells.',
        },
        {
          icon: '📈',
          title: 'Superior Survival Outcomes',
          text: 'Meta-analyses show ADCs deliver longer Overall Survival and Progression-Free Survival vs. standard chemotherapy regimens &mdash; with no increase in overall toxicity.',
        },
        {
          icon: '🧩',
          title: '15 Approved. 200+ in Development.',
          text: 'From the first FDA approval in 2000 to 15 approved therapies by 2022, the ADC pipeline has become the defining frontier of precision oncology.',
        },
      ]}
      highlight={`<p>The ADC market grew <strong style="color:var(--g);">${stats.growthPct}%</strong> from ${stats.firstQtrLabel} to ${stats.latestQtrLabel}. Every day without granular claims intelligence means navigating the fastest-evolving drug class in oncology history &mdash; blindfolded.</p>`}
      growthChart={{
        labels: stats.quarterLabels,
        datasets: [
          {
            data: quarterKeys.map((q) => data.quarterlyData[q].claims),
            borderColor: '#11a0a5',
            backgroundColor: 'rgba(17,160,165,.13)',
            pointHoverRadius: 3,
          },
        ],
        chartTitle: 'ADC Market Growth &mdash; U.S. Claims Volume',
        chartSubtitle: 'Quarterly claims &middot; IMX Data',
        firstValue: stats.firstQtrClaimsK,
        firstLabel: stats.firstQtrLabel,
        growthPctDisplay: `+${stats.growthPct}%`,
        latestValue: stats.latestQtrClaimsK,
        latestLabel: stats.latestQtrLabel,
        yTickFormat: (v) => formatCount(v),
        tooltipFormat: (v) => ` ${formatCount(v)} claims`,
        maxTicksLimit: 6,
      }}
    />

    <!-- SLIDE 1b: ADC VS CONVENTIONAL CHEMOTHERAPY -->
    <StudyTableSlide
      id="s1b"
      eyebrow="Chapter 01 &middot; The Challenge"
      headline="ADC vs. conventional chemotherapy"
      subtitle="Targeting, mechanism, cellular impact, survival, and toxicity"
      variant="compare"
      rows={adcChemoCompareRows}
    />

    <!-- SLIDE 2: THE MARKET -->
    <StudyMarketStatsSlide
      id="s2"
      active={slideActive('s2')}
      eyebrow="Chapter 02 &middot; The Market"
      headline="The ADC market is unlike anything<br />oncology has seen before."
      statTiles={[
        {
          value: stats.totalClaimsK,
          label: `Total U.S. Claims\n${stats.firstQtrLabel}–${stats.latestQtrLabel}`,
        },
        {
          value: `${stats.totalSpendM}+`,
          label: 'Total Market Spend\nAll-Time',
          valueColor: 'var(--pur)',
        },
        {
          value: `${stats.growthPct}%`,
          label: `Claim Volume Growth\nSince ${stats.firstQtrLabel}`,
          valueColor: 'var(--amb)',
        },
        { value: stats.latestQtrM, label: 'Market Spend\nLatest Quarter' },
      ]}
      doughnut={{
        type: 'pie',
        height: '155px',
        labels: manufacturerPayTop4WithOther.map((m) => m[0]),
        data: manufacturerPayTop4WithOther.map((m) => m[1]),
        colors: mfrPayColorsWithOther,
        tooltipFormat: (v, label, total) =>
          ` ${label}: ${formatSpend(v)} (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
        title: 'Total Pay by Manufacturer',
        subtitle: 'Top 4 + Other &middot; all-time ADC spend &middot; IMX Data',
      }}
      bar={{
        title: 'Claims per Year &mdash; by Manufacturer',
        subtitle: 'Daiichi Sankyo vs. Pfizer/Seagen &middot; annual claims &middot; IMX Data',
        labels: mfrYears,
        datasets: [
          {
            label: 'Daiichi Sankyo',
            data: mfrYears.map((y) => data.manufacturerYearlyData[y]?.['DAIICHI SANKYO'] ?? 0),
            backgroundColor: '#11a0a5',
            borderRadius: 2,
          },
          {
            label: 'Pfizer / Seagen',
            data: mfrYears.map((y) => data.manufacturerYearlyData[y]?.['PFIZER/SEAGEN'] ?? 0),
            backgroundColor: '#a78bfa',
            borderRadius: 2,
          },
        ],
        showLegend: true,
        yTickFormat: (v) => formatCount(v),
        tooltipFormat: (v, label) => ` ${label}: ${formatCount(v)}`,
        height: '160px',
      }}
    />

    <!-- SLIDE 2b: SPEND TRAJECTORY -->
    <StudySpendTrajectorySlide
      id="s2b"
      active={slideActive('s2b')}
      eyebrow="Chapter 02 &middot; The Market"
      headline={`<em>${stats.totalSpendM}</em> in ADC spend.<br />Here's how it happened.`}
      headlineStyle="margin-bottom:1rem;"
      chartTitle="Quarterly Claims by Manufacturer"
      chartSubtitle="Top manufacturers &middot; claim volume by quarter &middot; IMX Data"
      lineChart={{
        labels: stats.quarterLabels,
        datasets: top4MfrNames.map((name, i) => ({
          label: name,
          data: quarterKeys.map((q) => data.quarterlyData[q].manufacturerClaims[name] ?? 0),
          borderColor: mfrPayColors[i % mfrPayColors.length],
          backgroundColor: mfrPayFillColors[i % mfrPayFillColors.length],
          borderWidth: 2,
        })),
        height: '210px',
        yTickFormat: (v) => formatCount(v),
        tooltipFormat: (v, label) => ` ${label}: ${formatCount(v)} claims`,
        maxTicksLimit: 7,
      }}
      legendRows={top4MfrNames.map((name, i) => ({
        swatchStyle: `background:${mfrPayColors[i % mfrPayColors.length]};`,
        label: name,
      }))}
      statTiles={[
        {
          variant: 'stat-inline',
          value: stats.totalClaimsK,
          label: 'Total U.S. Claims\nAll-Time',
        },
        {
          variant: 'stat-inline',
          value: `${stats.totalSpendM}+`,
          label: 'Total Market Spend\nAll-Time',
          valueColor: 'var(--amb)',
        },
      ]}
      rightIntroHtml="Commercial consolidation &mdash; Pfizer/Seagen, AstraZeneca/Daiichi, AbbVie, and others drive claim volume as approvals and M&amp;A reshape the landscape. The lines show how each manufacturer's share of ADC claims has evolved quarter by quarter."
      timelineItems={[
        {
          year: '2000',
          body: `<strong>First ADC approved.</strong> Mylotarg (gemtuzumab ozogamicin) marks the class launch &mdash; Paul Ehrlich's &quot;magic bullets&quot; arrive in the clinic.`,
        },
        {
          year: '2019–21',
          body: '<strong>Enhertu &amp; Padcev approvals.</strong> HER2-directed and urothelial breakthroughs accelerate commercial adoption and plan formulary access.',
        },
        {
          year: 'Dec 2023',
          body: '<strong>Pfizer acquires Seagen for $43B.</strong> ADCs enter the strategic core of Big Pharma. Claims volume reflects the expanded commercial footprint.',
        },
        {
          year: 'Jan 2025',
          body: '<strong>Enhertu HER2-low/ultralow approval.</strong> $2,446M U.S. FY2025 in-market sales (+32% YoY); AstraZeneca U.S. product revenue from Enhertu $1,176M. Eligible patient population expands dramatically into HR+ breast cancer.',
        },
      ]}
      signal="Manufacturer claim share shifts in the same quarter as approvals and acquisitions — IMX data reflects commercial execution before earnings and public reports."
    />

    <!-- SLIDE 3: MEET IMX DATA -->
    <StudyMeetImxDataSlide
      id="s3"
      eyebrow="Chapter 03 &middot; The Solution"
      headline="Meet <em>IMX Data.</em><br />The granular claims layer<br />oncology has been missing."
      intro="IMX aggregates, normalizes, and delivers U.S. pharmaceutical claims at a depth no other provider matches &mdash; anonymized zip-code-level, claim-by-claim, day-by-day, segmented by ADC agent, payer, and patient demographics."
      gridTiles={adcMeetImxGridTiles}
      textCards={adcMeetImxTextCards}
    />

    <!-- SLIDE 3b: IMX DICTIONARY → THEMES -->
    <StudyTableSlide
      id="s3b"
      maxWidth="960px"
      eyebrow="Chapter 03 &middot; The Solution"
      headline="From IMX dictionary<br />to <em>your questions</em>"
      headlineStyle="margin-bottom:.55rem;"
      introHtml="The full IMX product resolves claim-level fields across identity, geography, economics, payer and plan, clinical coding, operations, and classification."
      subtitleAfterHr={true}
      subtitle="Analytic theme &rarr; representative IMX dictionary fields"
      subtitleStyle="margin:0 0 .75rem;font-size:.72rem;letter-spacing:.04em;color:var(--mu);text-transform:none;"
      hrStyle="margin-bottom:1rem;"
      variant="dictionary"
      rows={imxThemeFieldRows}
      footnoteHtml={adcImxDictionaryFootnoteHtml}
    />

    <!-- SLIDE 4: GEOGRAPHIC -->
    <StudyScoreBoardSlide
      id="s4"
      extraBottom
      eyebrow="Capability 01 &middot; Geographic Intelligence"
      headline="<em>Where</em> precision oncology gets prescribed<br />tells you everything about access."
      intro="Academic medical centers and NCI-designated cancer centers create massive geographic prescribing clusters. IMX Data maps ADC claim intensity down to the anonymized zip-code level, revealing which regions are ahead of &mdash; or behind &mdash; the clinical consensus."
      spotlightRows={adcS4SpotlightRows}
      insightHtml={adcS4InsightHtml}
      chartTitle="ADC Claims by State &mdash; Ranked"
      chartSubtitle="Top 10 states &middot; All-time total claims &middot; IMX Data"
      leaderboardRows={adcS4LeaderboardRows}
      calloutPct={`${top5Pct}%`}
      calloutBodyHtml={adcS4CalloutBodyHtml}
    />

    <!-- SLIDE 4b: CHOROPLETH -->
    <StudyChoroplethSlide
      id="s4b"
      eyebrow="Capability 02 &middot; Geographic Intelligence"
      headlineHtml="ADC claim intensity,<br /><em>state by state.</em>"
      stateData={data.stateData}
      topStates={data.stats.topStates}
      chartTitle="Total ADC Claims by State · IMX Data"
      chartSubtitle="Hover a state to see exact claim volume · IMX Data"
      calloutText="50 states tracked. Cancer center density drives geographic ADC adoption patterns visible days ahead of national data."
    />

    <!-- SLIDE 5: DRUG MIX -->
    <StudyMarketBattleSlide
      id="s5"
      extraBottom
      active={slideActive('s5')}
      eyebrow="Capability 03 &middot; Drug Mix &amp; Competitive Intelligence"
      headline="Know which ADC agents are gaining share<br /><em>before the clinical consensus forms.</em>"
      doughnut={{
        title: 'ADC Market Share by Claims Volume',
        subtitle: 'Top agents &middot; All-time &middot; IMX Data',
        labels: data.topDrugs.map((d) => d[0]),
        data: data.topDrugs.map((d) => d[1]),
        colors: colorPalette,
        cutout: '60%',
        tooltipFormat: (v, _label, total) =>
          ` ${formatCount(v)} claims (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
      }}
      battleTitle="Manufacturer Battle"
      battleSubtitle="AstraZeneca/Daiichi Sankyo vs. Pfizer/Seagen"
      battleLeft={{
        label: 'AZ / Daiichi',
        valueDisplay: stats.azdClaimsK,
        sharePct: stats.azdSharePct,
        barValue: stats.azdClaimsTotal,
      }}
      battleRight={{
        label: 'Pfizer / Seagen',
        valueDisplay: stats.pfizerClaimsK,
        sharePct: stats.pfizerSharePct,
        barValue: stats.pfizerClaimsTotal,
      }}
      volumeTitle="Agent-by-Agent Claim Volume"
      volumeSubtitle="All-time totals &middot; IMX Data"
      volumeRows={data.topDrugs}
      volumeMaxCount={stats.maxDrug}
      volumeBarColors={colorPalette}
      volumeFormatValue={formatCount}
      calloutBodyHtml={adcS5DrugMixCalloutHtml}
    />

    <!-- SLIDE 6: MANUFACTURER MIX -->
    <StudyMixCompare1Slide
      id="s6"
      active={slideActive('s6')}
      eyebrow="Capability 04 &middot; Manufacturer &amp; Commercial Intelligence"
      headline="<em>Who holds share</em> in ADC therapy<br />reflects M&amp;A and commercial execution."
      introHtml="Pfizer's $43B Seagen acquisition, AbbVie's ImmunoGen deal, and AstraZeneca/Daiichi's Enhertu dominance are reshaping the landscape. IMX tracks manufacturer claim share and spend quarter by quarter &mdash; so you see consolidation in real-world data first."
      leftCardTitle="All-Time Manufacturer Mix (Claims)"
      segments={adcS6MixSegments}
      totalCount={mfrClaimTotal}
      heroLabelHtml="Market Concentration"
      heroStat={`${top4MfrSharePct}%`}
      heroCaptionHtml="of ADC claims from top 4 manufacturers<br />M&amp;A and commercial execution show up in claims first"
      insightHtml="<strong>The signal isn't the level &mdash; it's the change.</strong> When a manufacturer's share moves after an approval or acquisition, IMX sees it the same quarter. Real-world claims intelligence ahead of earnings and public reports."
    />

    <!-- SLIDE 6b: PAYER MIX -->
    <StudyMixCompare2Slide
      id="s6b"
      active={slideActive('s6b')}
      eyebrow="Capability 05 &middot; Payer Mix Intelligence"
      headline="Medicare dominates ADC spend &mdash;<br /><em>but the shift is where the signal lives.</em>"
      introHtml="ADC patients skew older &mdash; Medicare is the dominant payer. Commercial, Medicaid, and government coverage shifts reveal access policy changes before they appear in public reports. IMX tracks payment type mix by quarter from oncology claims."
      leftCardTitle="All-Time Payer Mix"
      leftCardSubtitle="Claim volume by payment type &middot; IMX Data"
      payerRows={adcS6bPayerRows}
      totalCount={payTotal}
      {formatCount}
      heroLabelHtml="Insured Coverage"
      heroStat={`${stats.insuredSpendPct}%`}
      heroCaptionHtml="of ADC claims are payer-covered"
      insightHtml="<strong>The signal isn't the level &mdash; it's the change.</strong> When Medicare share compresses a point or two day-over-day, formulary access is expanding. IMX sees it first."
    />

    <!-- SLIDE 6c: CHARGE PER CLAIM DISTRIBUTION -->
    <StudyDistributionSlide
      id="s6c"
      active={slideActive('s6c')}
      eyebrow="Capability 05 &middot; Charge Profile"
      headline="<em>Per-claim</em> charge distribution &mdash;<br />percentiles and mean"
      introHtml="High-cost oncology ADC therapies produce a wide spread of charge amounts per claim. The interquartile band (P25&ndash;P75) captures where half of claims sit; the median is the midpoint, while the <strong>mean</strong> reflects the upward pull of high-cost tail claims. The scale runs from zero to a headroom above the upper percentiles and mean."
      distribution={data.chargeDistribution}
      {formatSpend}
      stripTitle="Payment per claim &mdash; charge amount distribution"
      insightHtml="<strong>Why it matters:</strong> Benchmarking access programs, benefit design, and out-of-pocket exposure starts with the shape of the claim distribution &mdash; not just averages."
    />

    <!-- SLIDE 7: DEMOGRAPHICS -->
    <StudyPatientDemographicsSlide
      id="s7"
      active={slideActive('s7')}
      eyebrow="Capability 06 &middot; Patient Demographics"
      headline="Real patients. Real oncology data.<br /><em>Real answers.</em>"
      ageBarTitle="ADC Patients by Age Cohort"
      ageBarSubtitle="All-time patients by decade born &middot; IMX Data"
      ageLabels={data.ageCohorts.map((a) => a[0])}
      ageBarData={data.ageCohorts.map((a) => a[1])}
      ageBarColors={colorPalette}
      ageYTickFormat={formatCount}
      ageTooltipFormat={(v) => ` ${formatCount(v)} patients`}
      genderDoughnut={{
        title: `Gender Split &mdash; ${stats.totalGen.toLocaleString()} Patients`,
        subtitle: `${stats.femalePct}% Female &middot; ${stats.malePct}% Male &middot; ${stats.unknownPct}% Unknown`,
        labels: ['Female', 'Male', 'Unknown'],
        data: [...data.genderTotals],
        colors: ['#E8634A', '#11a0a5', '#2d3a37'],
        cutout: '55%',
        tooltipFormat: (v, _label, total) =>
          ` ${v.toLocaleString()} (${total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'}%)`,
        height: '122px',
      }}
      narrativeCards={adcS7NarrativeCards}
      insightHtml={adcS7InsightHtml}
    />

    <!-- SLIDE 7b: DIAGNOSIS MIX -->
    <StudyDiagnosisBarInsightSlide
      id="s7b"
      active={slideActive('s7b')}
      eyebrow="Capability 06 &middot; Clinical Context"
      headline="Every claim encodes a clinical reason.<br /><em>Diagnosis codes at scale.</em>"
      chartTitle="Top diagnosis codes &amp; ADC J-codes"
      chartSubtitleJcodesHtml={adcS7bChartSubtitleJcodesHtml}
      horizontalBar={{
        labels: diagnosisChartRows.map((d) => d.code),
        data: diagnosisChartRows.map((d) => d.claims),
        backgroundColor: diagnosisChartRows.map((_, i) => colorPalette[i % colorPalette.length]),
        height: '420px',
        yTickFormat: formatCount,
        tooltipFormat: (v) => formatCount(v) + ' claims',
        tooltipCategoryTitles: diagnosisChartRows.map((d) => d.label),
        tooltipExtraLines: diagnosisChartRows.map((d) => 'Code: ' + d.code),
      }}
      narrativeCard={{
        icon: '📋',
        title: 'Claims follow the coding',
        text: 'Chemotherapy and immunotherapy encounter codes often lead volume; oncology-specific neoplasm codes reflect tumor site and stage of care. IMX surfaces the full diagnosis distribution behind ADC utilization &mdash; not just drug names.',
      }}
      insightHtml="<strong>Why it matters:</strong> Labeling and prior-authorization narratives tie to these codes; shifts here precede shifts in prescribing and access."
    />

    <!-- SLIDE 8: M&A AND OUTLOOK -->
    <StudyMarketOutlookSlide
      id="s8"
      active={slideActive('s8')}
      eyebrow="Chapter 04 &middot; The M&amp;A Revolution"
      headline="$63B+ in acquisitions.<br />ADCs are <em>Big Pharma's core bet.</em>"
      eyebrowStyle="margin-bottom:0.55rem;"
      headlineStyle="margin-bottom:1rem;"
      tcStyle="gap:1.4rem;align-items:start;"
      timelineItems={adcS8TimelineItems}
      spendLineTitle="Total ADC Spend Trajectory"
      spendLineSubtitle="$USD quarterly &middot; IMX Data"
      quarterLabels={stats.quarterLabels}
      spendLineValues={adcS8SpendLineValues}
      lineChartHeight="178px"
      lineYTickFormat={(v) => '$' + v + 'M'}
      lineTooltipFormat={(v) => ` $${v.toFixed(1)}M`}
      signalCalloutHtml={adcS8SignalCalloutHtml}
      signalCalloutParagraphStyle="margin:0;font-size:.82rem;line-height:1.42;"
      rightColumnStyle="display:flex;flex-direction:column;gap:.72rem;"
      statTileVariant="pipeline"
      statTiles={[
        { value: '200+', label: 'ADC Assets in\nDevelopment' },
        { value: '$32.11B', label: 'Global ADC Market\nby 2033', valueColor: 'var(--amb)' },
      ]}
      tilesGridStyle="display:grid;grid-template-columns:1fr 1fr;gap:.62rem;"
    />

    <!-- SLIDE 9: CLOSE -->
    <StudyClosingSlide
      id="s9"
      {goTo}
      {disableLinks}
      headlineHtml="<em>The oncology data edge</em><br />that changes<br />everything."
      leadHtml="IMX Data delivers the depth behind the headlines."
      taglineHtml="Every ADC &nbsp;&middot;&nbsp; Every state &nbsp;&middot;&nbsp; Every day."
      footerHtml={adcClosingFooterHtml}
    />
  {/snippet}
</StudyPresentation>

<style>
/* ── Slide s8: tighter vertical rhythm so centered block fits better on short viewports ── */
:global(.study-presentation .slide#s8 .ey) {
  margin-bottom: 0.55rem;
}
:global(.study-presentation .slide#s8 .hl) {
  margin-bottom: 0.7rem;
}
:global(.study-presentation .slide#s8 .tc) {
  gap: 1rem;
}
:global(.study-presentation .slide#s8 .tli) {
  padding: 0.52rem 0.78rem;
  margin-bottom: 0.34rem;
}
:global(.study-presentation .slide#s8 .tt) {
  font-size: 0.72rem;
  line-height: 1.42;
}
@media (max-height: 900px) {
  :global(.study-presentation .slide#s8 .ch) {
    height: 148px !important;
  }
  :global(.study-presentation .slide#s8 .hb) {
    padding: 0.85rem 1rem;
  }
  :global(.study-presentation .slide#s8 .hb p) {
    font-size: 0.78rem;
    line-height: 1.55;
  }
}

/* s2b + s3 + s7: laptop-height viewports — tighter cards / charts so content clears the fixed nav */
@media (min-width: 1025px) and (max-height: 900px) {
  :global(.study-presentation .slide#s2b .tc),
  :global(.study-presentation .slide#s7 .tc) {
    gap: 1rem !important;
  }
  :global(.study-presentation .slide#s2b .tc > div),
  :global(.study-presentation .slide#s7 .tc > div) {
    gap: 0.55rem !important;
  }
  :global(.study-presentation .slide#s2b .ey),
  :global(.study-presentation .slide#s7 .ey) {
    margin-bottom: 0.32rem !important;
  }
  :global(.study-presentation .slide#s2b .hl),
  :global(.study-presentation .slide#s7 .hl) {
    margin-bottom: 0.65rem !important;
  }
  :global(.study-presentation .slide#s2b .cc),
  :global(.study-presentation .slide#s7 .cc) {
    padding: 0.78rem 0.9rem 0.72rem !important;
  }
  :global(.study-presentation .slide#s2b .ch) {
    height: 168px !important;
  }
  :global(.study-presentation .slide#s7 .tc > div:first-child > .cc:first-child .ch) {
    height: 152px !important;
  }
  :global(.study-presentation .slide#s7 .tc > div:first-child > .cc:nth-child(2) .ch) {
    height: 100px !important;
  }
  :global(.study-presentation .slide#s2b .sb) {
    padding: 0.65rem 0.7rem !important;
  }
  :global(.study-presentation .slide#s2b .sb .n) {
    font-size: 1.28rem !important;
  }
  :global(.study-presentation .slide#s2b .bd) {
    margin-bottom: 0.65rem !important;
  }
  :global(.study-presentation .slide#s2b .tli),
  :global(.study-presentation .slide#s7 .tli) {
    padding: 0.55rem 0.78rem !important;
    margin-bottom: 0.34rem !important;
  }
  :global(.study-presentation .slide#s2b .hb),
  :global(.study-presentation .slide#s7 .hb) {
    padding: 0.88rem 0.95rem !important;
  }
  :global(.study-presentation .slide#s7 .ca) {
    padding: 0.95rem 0.95rem !important;
  }
  :global(.study-presentation .slide#s7 .ca .ic) {
    font-size: 1.05rem !important;
    margin-bottom: 0.4rem !important;
  }
  :global(.study-presentation .slide#s7 .ca .vl) {
    font-size: 1.35rem !important;
  }

  :global(.study-presentation .slide#s3 .tc.w) {
    gap: 1.35rem !important;
  }
  :global(.study-presentation .slide#s3 .ey) {
    margin-bottom: 0.32rem !important;
  }
  :global(.study-presentation .slide#s3 .hl) {
    margin-bottom: 0.55rem !important;
  }
  :global(.study-presentation .slide#s3 .bd) {
    margin-bottom: 0.75rem !important;
    font-size: 0.84rem !important;
    line-height: 1.6 !important;
  }
  :global(.study-presentation .slide#s3 .bd + div) {
    gap: 0.5rem !important;
  }
  :global(.study-presentation .slide#s3 .sb) {
    padding: 0.65rem 0.7rem !important;
  }
  :global(.study-presentation .slide#s3 .sb .n) {
    font-size: 1.28rem !important;
  }
  :global(.study-presentation .slide#s3 .sb .l) {
    font-size: 0.56rem !important;
    margin-top: 0.32rem !important;
  }
  :global(.study-presentation .slide#s3 .tc.w > div:last-child) {
    gap: 0.48rem !important;
  }
  :global(.study-presentation .slide#s3 .ca) {
    padding: 0.82rem 0.88rem !important;
  }
  :global(.study-presentation .slide#s3 .ca h4) {
    font-size: 0.76rem !important;
    margin-bottom: 0.2rem !important;
  }
  :global(.study-presentation .slide#s3 .ca p) {
    font-size: 0.71rem !important;
    line-height: 1.5 !important;
  }
}

:global(.adc-key-sources) {
  margin-top: 1.35rem;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
  border-top: 1px solid var(--table-border-t, rgba(255, 255, 255, 0.08));
  padding-top: 0.85rem;
}
:global(.adc-key-sources summary) {
  cursor: pointer;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--mu, rgba(255, 255, 255, 0.45));
  list-style: none;
}
:global(.adc-key-sources summary::-webkit-details-marker) {
  display: none;
}
:global(.adc-key-sources summary::after) {
  content: ' +';
  font-weight: 600;
  color: var(--g, #11a0a5);
}
:global(.adc-key-sources[open] summary::after) {
  content: ' −';
}
:global(.adc-key-sources ul) {
  margin: 0.65rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.72rem;
  line-height: 1.55;
  color: var(--t2, rgba(255, 255, 255, 0.78));
}
:global(.adc-key-sources a) {
  color: var(--g, #11a0a5);
  text-decoration: none;
}
:global(.adc-key-sources a:hover) {
  text-decoration: underline;
}

/* ── s2: pie + bar chart cards proportional on mobile ── */
@media (max-width: 640px) {
  :global(.study-presentation .slide#s2 .tc .ch) {
    height: 135px !important;
  }
}

/* ── s2b: quarterly line chart not oversized when stacked 1-column on mobile ── */
@media (max-width: 640px) {
  :global(.study-presentation .slide#s2b .cc .ch) {
    height: 160px !important;
  }
}
@media (max-width: 430px) {
  :global(.study-presentation .slide#s2b .cc .ch) {
    height: 130px !important;
  }
}
</style>
